import React, { Fragment, useCallback, useState } from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { CUSTOMER_TYPE_COMPANY } from '../constants';
import constants from '../constants';
import InputField from './InputField';
import { userDetailsCompanySchema, userDetailsSchema } from './MyAcount.ValidationSchema';
import { saveUserInformation, submitUserInfoError, clearUserInfoError } from '../Actions/MyAccount.action';
import { getCountryName, getCountryStates, getCountryVatNumberPrefix, isSalesTaxCountry } from '../Services/Country.service';
import { addFlashMessage } from '../Actions/FlashMessages.action';

const validateUserDetails = async (userDetails, forceAddress) => {
    const validationResult = await userDetailsSchema.validate(
        userDetails, 
        {
            context: { forceAddress },
            abortEarly: false,
        },
    );
    if (userDetails.customerType == 'company') {
        return await userDetailsCompanySchema.validate(validationResult, { abortEarly: false })
    }
    return validationResult;
};

const MyAccountUserInfo = () => {
    const dispatch = useDispatch();
    const { payload, errors = {} } = useSelector((state) => state.myAccountUserInfo);
    const { 
        customerId, 
        customerTypes, 
        isBusinessCustomer, 
        hasActiveFlowSubscription,
        userDetails } = payload;

    const [formData, setFormData] = useState(userDetails);
    const [isEditing, setIsEditing] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [countryStates, setCountryStates] = useState(getCountryStates(userDetails.country));
    const [vatNumberPrefix, setVatNumberPrefix] = useState(getCountryVatNumberPrefix(userDetails.country));
    const [isSaving, setIsSaving] = useState(false);
    
    const currentUserCountry = getCountryName(userDetails.country);
    const forceAddress = hasActiveFlowSubscription && isSalesTaxCountry(formData.country);

    const handleCancel = (event) => {
        event.preventDefault();
        setFormData(userDetails);
        dispatch(clearUserInfoError());
        setIsModified(false);
        setIsEditing(false);
    }

    const handleFieldChange = (name, value) => {
        setFormData((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
        setIsModified(true);
    }

    const handleCountryChange = (countryCode) => {
        setFormData((currentForm) => ({
            ...currentForm,
            'country': countryCode,
            'state': '',
        }));
        setIsModified(true);

        if (countryCode) {
            setCountryStates(getCountryStates(countryCode));
            var vatNumberPrefix = getCountryVatNumberPrefix(countryCode);
            if (!vatNumberPrefix) {
                setFormData((currentForm) => ({
                    ... currentForm,
                    'customerType': 'private'
                }));
            }
            setVatNumberPrefix(vatNumberPrefix);
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!isModified) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        dispatch(clearUserInfoError());

        try {
            await validateUserDetails(formData, forceAddress);
            await dispatch(saveUserInformation(formData));
            dispatch(addFlashMessage({ 
                messageText: translate('js.myaccount.settings.message.infoupdatesuccess'), 
                messageType: "Green", 
                dismissible: true, 
                timeToLive: 10 }))
            setIsModified(false);
            setIsEditing(false);
        } catch (ex) {
            dispatch(submitUserInfoError(ex));
        }
        finally {
            setIsSaving(false);
        }
    }

    return (
        <Fragment>
            <div className="my-account-settings__component user-information">
            <h2>{translate('js.myaccount.settings.heading.information')}</h2>
            {!isEditing ? 
                (<div>
                    <address>
                        <span className="user-information__bottom-margin">
                            {translate('js.myaccount.settings.summary.customerid')} {customerId}
                        </span>
                        {userDetails.customerType === CUSTOMER_TYPE_COMPANY && userDetails.companyName && <span>{userDetails.companyName}</span>}
                        {userDetails.customerType === CUSTOMER_TYPE_COMPANY && userDetails.vatNumber && <span>{userDetails.vatNumber}</span>}
                        {(userDetails.firstName || userDetails.lastName) && <span>{userDetails.firstName} {userDetails.lastName}</span>}
                        {userDetails.address && <span>{userDetails.address}</span>}
                        {userDetails.zipCode && <span>{userDetails.zipCode}</span>}
                        {currentUserCountry && <span>{currentUserCountry}</span>}
                        {userDetails.state && <span>{userDetails.state}</span>}
                        {isBusinessCustomer && <span className="user-information__top-margin">{userDetails.email}</span>}
                        {userDetails.phoneNumber && <span className={!isBusinessCustomer ? "user-information__top-margin" : ""}>{userDetails.phoneNumber}</span> }
                    </address>
                    {!isBusinessCustomer &&
                        <button onClick={() => setIsEditing(true)} className="button button__link">
                            {translate('js.myaccount.settings.button.changeinformation')}
                        </button>}
                </div>)
                :
                (<form onSubmit={handleSubmit}>
                    {(customerTypes?.length > 0 && vatNumberPrefix) && (
                        <Fragment>
                            <div className="form-radio-group">
                                {customerTypes.map((customerType) => (
                                    <InputField
                                    key={customerType}
                                    id={`customerType-${customerType}`}
                                    name='customerType'
                                    type='radio'
                                    value={customerType}
                                    checked={formData.customerType === customerType}
                                    label={translate(`js.checkout.customerdetails.label.customertype.${customerType}`)}
                                    onChange={(value) => handleFieldChange('customerType', value)}
                                    errors={errors}
                                    />
                                ))}
                            </div>
                            { formData.customerType === CUSTOMER_TYPE_COMPANY && (
                                <Fragment>
                                    <InputField
                                        id='companyName'
                                        name='companyName'
                                        type='text'
                                        value={formData.companyName}
                                        required={true}
                                        label={translate('js.myaccount.settings.label.companyname')}
                                        onChange={(value) => handleFieldChange('companyName', value)}
                                        errors={errors}
                                    />
                                    <InputField
                                        id='vatNumber'
                                        name='vatNumber'
                                        type='text'
                                        value={formData.vatNumber}
                                        required={true}
                                        label={translate('js.myaccount.settings.label.vatnumber')}
                                        onChange={(value) => handleFieldChange('vatNumber', value)}
                                        errors={errors}
                                        textInputPrefix={vatNumberPrefix}
                                    />
                                </Fragment>
                            )}
                        </Fragment>
                    )}
                    <InputField
                        id='firstName'
                        name='firstName'
                        type='text'
                        value={formData.firstName}
                        required={forceAddress}
                        label={translate('js.myaccount.settings.label.firstname')}
                        onChange={(value) => handleFieldChange('firstName', value)}
                        errors={errors}
                    />
                    <InputField
                        id='lastName'
                        name='lastName'
                        type='text'
                        value={formData.lastName}
                        required={forceAddress}
                        label={translate('js.myaccount.settings.label.lastname')}
                        onChange={(value) => handleFieldChange('lastName', value)}
                        errors={errors}
                    />
                    <InputField
                        id='address'
                        name='address'
                        type='text'
                        value={formData.address}
                        required={forceAddress}
                        label={translate('js.myaccount.settings.label.address')}
                        onChange={(value) => handleFieldChange('address', value)}
                        errors={errors}
                        containerCssClass='full-row'
                    />
                    <InputField
                        id='city'
                        name='city'
                        type='text'
                        value={formData.city}
                        required={forceAddress}
                        label={translate('js.myaccount.settings.label.city')}
                        onChange={(value) => handleFieldChange('city', value)}
                        errors={errors}
                    />
                    <InputField
                        id='zipCode'
                        name='zipCode'
                        type='text'
                        value={formData.zipCode}
                        required={forceAddress}
                        label={translate('js.myaccount.settings.label.zip')}
                        onChange={(value) => handleFieldChange('zipCode', value)}
                        errors={errors}
                    />
                    <label className="full-row">
                        <span className="label-required">{translate('js.myaccount.settings.label.country')}</span>
                        <select
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={(event) => handleCountryChange(event.target.value)}
                        >
                            <option disabled value="">{translate('js.myaccount.settings.defaultoption.country')}</option>
                            {constants.countries.map(
                                ({ name, code }) => (
                                    <option value={code} key={`country-${code}`}>{name}</option>
                                )
                            )}
                        </select>
                        {errors["country"] && 
                            <div className="validation-message" data-error-for="country">
                                {errors["country"][0]}
                            </div>
                        }
                    </label>
                    {(countryStates && countryStates.length > 0) &&
                        <label className="half-row">
                            <span className="label-required">{translate('js.myaccount.settings.label.state')}</span>
                            <select
                                id="state"
                                name="state"
                                value={formData.state}
                                onChange={(event) => handleFieldChange('state', event.target.value)}
                            >
                                <option disabled value="">{translate('js.myaccount.settings.defaultoption.state')}</option>
                                {countryStates.map(
                                    ({ name, code }) => (
                                        <option value={code} key={`state-${code}`}>{name}</option>
                                    )
                                )}
                            </select>
                            {errors["state"] && 
                                <div className="validation-message" data-error-for="state">
                                    {errors["state"][0]}
                                </div>
                            }
                        </label>
                    }
                    <InputField
                        id='phoneNumber'
                        name='phoneNumber'
                        type='text'
                        value={formData.phoneNumber}
                        label={translate('js.myaccount.settings.label.phonenumber')}
                        onChange={(value) => handleFieldChange('phoneNumber', value)}
                        errors={errors}
                        containerCssClass='half-row'
                    />
                    <div>
                        <button type="submit" className={`button button__primary ${isSaving && 'loading'}`} disabled={isSaving}>
                            <span>{translate('js.myaccount.settings.button.saveinformation')}</span>
                            <span className="spinner spinner--light spinner--small"></span>
                        </button>
                        <button className="button button__outline" disabled={isSaving} onClick={handleCancel}>{translate("js.myaccount.settings.button.cancel")}</button>
                    </div>
                </form>
            )}
            </div>
        </Fragment>
    )
}

export default MyAccountUserInfo;